import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
  useState,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Input sizes
 */
type InputSize = 'sm' | 'md' | 'lg';

/**
 * Props for the Input component
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Error message - when present, shows error state */
  error?: string;
  /** Size variant of the input */
  size?: InputSize;
  /** Icon to display at the start of the input */
  leftIcon?: ReactNode;
  /** Icon to display at the end of the input */
  rightIcon?: ReactNode;
  /** Full width input */
  fullWidth?: boolean;
  /** Whether the field is required */
  required?: boolean;
}

/**
 * Size-specific styles for the input
 */
const sizeStyles: Record<InputSize, { input: string; label: string; icon: string }> = {
  sm: {
    input: 'px-3.5 py-2 text-sm rounded-lg',
    label: 'text-xs mb-1.5',
    icon: 'w-4 h-4',
  },
  md: {
    input: 'px-4 py-3 text-base rounded-xl',
    label: 'text-sm mb-2',
    icon: 'w-5 h-5',
  },
  lg: {
    input: 'px-5 py-3.5 text-lg rounded-2xl',
    label: 'text-base mb-2.5',
    icon: 'w-6 h-6',
  },
};

/**
 * Animation variants for helper/error text
 */
const textVariants = {
  hidden: { opacity: 0, y: -8, height: 0 },
  visible: { opacity: 1, y: 0, height: 'auto' },
  exit: { opacity: 0, y: -8, height: 0 },
};

/**
 * Input component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email Address"
 *   placeholder="Enter your email"
 *   helperText="We'll never share your email"
 * />
 *
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password must be at least 8 characters"
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      size = 'md',
      leftIcon,
      rightIcon,
      fullWidth,
      required,
      disabled,
      className,
      id: providedId,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = providedId || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    const [isFocused, setIsFocused] = useState(false);

    const hasError = Boolean(error);
    const showHelper = helperText && !hasError;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <div className={cn('flex flex-col', fullWidth && 'w-full', className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              sizeStyles[size].label,
              'font-medium text-surface-800',
              disabled && 'opacity-50'
            )}
          >
            {label}
            {required && (
              <span className="text-error ml-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        {/* Input Container */}
        <motion.div
          className={cn(
            'relative flex items-center',
            'backdrop-blur-xl',
            'bg-white/[0.08]',
            'border',
            hasError
              ? 'border-error/60'
              : isFocused
              ? 'border-primary/60'
              : 'border-white/[0.12]',
            'transition-all duration-300',
            sizeStyles[size].input,
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          animate={{
            boxShadow: isFocused
              ? hasError
                ? '0 0 0 3px rgba(239, 68, 68, 0.15), 0 4px 20px rgba(0, 0, 0, 0.3)'
                : '0 0 0 3px rgba(72, 149, 239, 0.15), 0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 4px 20px rgba(0, 0, 0, 0.2)',
          }}
          transition={{ duration: 0.2 }}
        >
          {/* Left Icon */}
          {leftIcon && (
            <span
              className={cn(
                sizeStyles[size].icon,
                'flex-shrink-0 mr-2',
                hasError ? 'text-error' : 'text-surface-500'
              )}
            >
              {leftIcon}
            </span>
          )}

          {/* Input Element */}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            required={required}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : showHelper ? helperId : undefined}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cn(
              'flex-1 bg-transparent outline-none',
              'text-surface-900 placeholder:text-surface-500',
              'disabled:cursor-not-allowed'
            )}
            {...props}
          />

          {/* Right Icon */}
          {rightIcon && (
            <span
              className={cn(
                sizeStyles[size].icon,
                'flex-shrink-0 ml-2',
                hasError ? 'text-error' : 'text-surface-500'
              )}
            >
              {rightIcon}
            </span>
          )}
        </motion.div>

        {/* Helper Text / Error Message */}
        <AnimatePresence mode="wait">
          {hasError ? (
            <motion.p
              key="error"
              id={errorId}
              role="alert"
              variants={textVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="mt-1.5 text-sm text-error flex items-center gap-1"
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </motion.p>
          ) : showHelper ? (
            <motion.p
              key="helper"
              id={helperId}
              variants={textVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2 }}
              className="mt-1.5 text-sm text-surface-500"
            >
              {helperText}
            </motion.p>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
